import { useCallback, useState } from 'react';
import { useAxiosInstance } from '@gov-nx/api';
import type { DownloadEPetitionAttachmentSubscription } from '@gov-nx/core/events';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { usePetitionAttachmentDownload } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { DownloadCallback } from '../Download/types';

export const DownloadEPetitionAttachmentListener = () => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Service);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { download } = useDownloadEvents();
	const { axiosInstance } = useAxiosInstance();
	const [callbacks, setCallbacks] = useState<{
		[key: number]: Nullable<DownloadCallback<number>['callback']>;
	}>({});

	const { mutate } = usePetitionAttachmentDownload({
		onSuccess: (data) => {
			download({
				blob: data.blob,
				id: data.petitionId.toString(),
				antivirus: data.antivirus,
				fileName: data.fileName,
				callback: (id, success, error) => {
					if (success) {
						successMessage(data.fileName);
						onDownload(data.attachmentId);
					}
					if (error) {
						errorMessage();
						onDownload(data.attachmentId, error);
					}
				},
			});
		},
		onError: () => {
			errorMessage();
		},
	});

	const onEPetitionAttachmentDownload = useCallback(
		(
			_: DownloadEPetitionAttachmentSubscription['type'],
			{
				attachmentData,
				callback,
			}: DownloadEPetitionAttachmentSubscription['payload']
		) => {
			if (callback) {
				setCallbacks((state) => ({
					...state,
					[attachmentData.attachmentId]: callback,
				}));
			}

			setTimeout(() => {
				mutate({ ...attachmentData, axiosInstance });
			}, 0);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[mutate]
	);

	useDownloadEvents({
		onEPetitionAttachmentDownload,
	});

	const onDownload = useCallback(
		(attachmentId: number, error?: Error) => {
			const callbackFunction = callbacks[attachmentId];
			if (callbackFunction) {
				callbackFunction(attachmentId, !error, error ?? null);
				setCallbacks((state) => ({ ...state, [attachmentId]: null }));
			}
		},
		[callbacks]
	);

	const successMessage = (fileName: string) => {
		toastMessageSuccess(
			t('dokument.stazeni-dokumentu-uspech', {
				fileName,
			})
		);
	};

	const errorMessage = () => {
		toastMessageError(t('dokument.stazeni-dokumentu-chyba'));
	};

	return null;
};
